import { useState } from 'react';
import { useSheet, useToast } from '../app/shell-context';
import { ApiError } from '../api/client';
import { Notice } from '../ui/parts';
import { ChevronIcon } from '../ui/icons';
import { useConnectBot, usePlatformContacts, useSendInvite, useSetSubscription } from './api';

/**
 * Отправка ссылки-приглашения. Бот не может написать первым тому, кто ему не
 * писал — ни по номеру, ни по @username. Поэтому адресат выбирается из тех, кто
 * уже открывал бота, а отказ (`sent: false`) — не ошибка: показываем ссылку.
 */
export function InviteSheet({ tenantId, title, link, code }: {
  tenantId: string;
  title: string;
  link: string | null;
  code?: string;
}) {
  const sheet = useSheet();
  const toast = useToast();
  const contacts = usePlatformContacts();
  const send = useSendInvite();
  const [result, setResult] = useState<{ sent: boolean; link: string; error?: string } | null>(null);

  const list = contacts.data ?? [];
  const shownLink = result?.link ?? link;

  return (
    <>
      <h3>Отправить ссылку</h3>
      <p>
        Бот пишет только тем, кто ему писал. Если человека нет в списке — скопируйте ссылку и
        отправьте сами: в мессенджере, по почте, как угодно.
      </p>

      {result ? (
        <Notice kind={result.sent ? 'calm' : 'warn'}>
          {result.sent
            ? 'Ссылка отправлена в Telegram.'
            : 'Бот не смог написать этому человеку — он не открывал бота или заблокировал его. Отправьте ссылку сами.'}
        </Notice>
      ) : null}

      {!result?.sent && list.length ? (
        <>
          <p className="eyebrow">Кому бот уже может писать</p>
          <div className="rows">
            {list.map((c) => (
              <button
                className="rw"
                key={c.tg_id}
                disabled={send.isPending}
                onClick={() =>
                  send.mutate(
                    { id: tenantId, chatId: c.tg_id, code },
                    {
                      onSuccess: (res) => {
                        setResult(res);
                        toast(res.sent ? 'Ссылка отправлена' : 'Бот написать не смог — ссылка ниже');
                      },
                      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отправить'),
                    },
                  )
                }
              >
                <span className="rw-body">
                  <b>{c.first_name || 'Без имени'}</b>
                  <span>{c.username ? `@${c.username}` : `id ${c.tg_id}`}</span>
                </span>
                <span className="chev"><ChevronIcon /></span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {!list.length && !result ? (
        <Notice>
          Боту пока никто не писал, поэтому отправить некому. Скопируйте ссылку и передайте её сами.
        </Notice>
      ) : null}

      {shownLink ? (
        <>
          <div className="field">
            <label htmlFor="invLink">Ссылка-приглашение</label>
            <input id="invLink" type="text" readOnly value={shownLink} onFocus={(e) => e.currentTarget.select()} />
            <span className="hint">
              Одноразовая, 14 дней. По ней человек попадёт сразу в админку своей организации.
            </span>
          </div>
          <div className="field">
            <label>Что увидит адресат</label>
            <div className="card" style={{ fontSize: '13.5px', lineHeight: 1.5 }}>
              Вы приглашены администратором в «{title}». Откройте ссылку — попадёте сразу в админку
              своей организации: {shownLink}
            </div>
          </div>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(shownLink);
              toast('Ссылка скопирована');
            }}
          >
            Скопировать ссылку
          </button>
        </>
      ) : (
        <Notice>
          Ссылку собрать не из чего: бота нет ни у организации, ни у платформы. Подключите бота — и
          приглашение появится здесь.
        </Notice>
      )}

      <button className="btn solid" onClick={() => sheet.close()}>Готово</button>
    </>
  );
}

/**
 * Сеть и токен — разные беды с разными лекарствами: одну чинит владелец у BotFather,
 * другую администратор сервера. Поэтому и текст разный.
 */
function connectError(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Не получилось подключить';
  if (e.is('TELEGRAM_UNREACHABLE')) return `Нет связи с Telegram: ${e.message}`;
  if (e.is('BOT_TAKEN')) return e.message;
  return `Telegram не принял токен: ${e.message}`;
}

/** Подключение бота организации: токен проверяется через getMe, вебхук ставится сервером. */
export function ConnectBotSheet({ tenantId, replace = false }: { tenantId: string; replace?: boolean }) {
  const sheet = useSheet();
  const toast = useToast();
  const connect = useConnectBot();
  const [token, setToken] = useState('');

  return (
    <>
      <h3>{replace ? 'Замена бота организации' : 'Подключение бота организации'}</h3>
      <p>
        Создайте {replace ? 'нового бота' : 'бота'} через @BotFather и вставьте его токен ниже. Мы
        проверим бота, подключим его к организации и автоматически настроим webhook.
      </p>
      <div className="field">
        <label htmlFor="pbToken">Токен бота</label>
        <input id="pbToken" type="text" placeholder="123456:AA..." value={token} onChange={(e) => setToken(e.target.value)} />
        <span className="hint">
          Токен хранится на сервере и используется только для работы бота. При необходимости его можно
          заменить в любой момент.
        </span>
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={token.trim().length < 20 || connect.isPending}
          onClick={() =>
            connect.mutate(
              { id: tenantId, botToken: token.trim() },
              {
                onSuccess: (res) => {
                  toast(res.username ? `Бот @${res.username} подключён` : 'Бот подключён');
                  sheet.close();
                },
                onError: (e) => toast(connectError(e)),
              },
            )
          }
        >
          {connect.isPending ? 'Проверяем…' : replace ? 'Проверить и заменить' : 'Проверить и подключить'}
        </button>
      </div>
    </>
  );
}

const PLANS: { value: string; label: string; note: string }[] = [
  { value: 'trial', label: 'Пробный период', note: 'Все функции доступны. Оплата пока не требуется.' },
  { value: 'active', label: 'Активна', note: 'Обычная работа организации.' },
  { value: 'past_due', label: 'Просрочена', note: 'Оплата задержана, но организация ещё работает.' },
  { value: 'readonly', label: 'Только чтение', note: 'Записи видны, напоминания уходят, новых записей не создать.' },
  { value: 'cancelled', label: 'Расторгнута', note: 'То же, что «только чтение», но без возврата к оплате.' },
];

/** Тариф. Неоплата не отключает центр — иначе пострадают родители, а не он. */
export function PlanSheet({ tenantId, title, status }: { tenantId: string; title: string; status: string }) {
  const sheet = useSheet();
  const toast = useToast();
  const save = useSetSubscription();
  const [value, setValue] = useState(status);
  const plan = PLANS.find((p) => p.value === value);

  return (
    <>
      <h3>Тариф · {title}</h3>
      <p>
        При окончании подписки организация продолжит работать в ограниченном режиме: существующие
        записи и напоминания сохранятся, но создание новых записей будет недоступно.
      </p>
      <div className="field">
        <label htmlFor="ppPlan">Состояние подписки</label>
        <select id="ppPlan" value={value} onChange={(e) => setValue(e.target.value)}>
          {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <span className="hint">{plan?.note}</span>
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={value === status || save.isPending}
          onClick={() =>
            save.mutate(
              { id: tenantId, status: value },
              {
                onSuccess: () => {
                  toast('Тариф изменён');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось изменить'),
              },
            )
          }
        >
          Применить
        </button>
      </div>
    </>
  );
}
